import { Button } from '@mui/material';
import { DataGrid, GridAddIcon, type GridColDef } from '@mui/x-data-grid';
import { Outlet } from 'react-router';
import { useEffect, useState } from 'react';
import moment from 'moment';

import PageBaseLayout from '../layouts/PageBaseLayout';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import useDialog from '../hooks/useDialog';
import { supabase } from '../supabase-client';
import type { BrandWithdrawalRequest } from '../models/types';
import CreateWithdrawalRequestDialog from '../components/CreateWithdrawalRequestDialog';

/**
 * Brand-facing withdrawal requests page (merchant_admin only). Lists the
 * merchant's own requests with their statuses and lets them submit a new one.
 * Rows come from `admin_backend.brand_withdrawal_requests`, scoped to the caller
 * by RLS. Approving/rejecting is the tranding_admin's job on the separate
 * `TrandingBrandWithdrawalRequests` page.
 */
export default function BrandWithdrawalRequests() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const [withdrawalRequests, setWithdrawalRequests] = useState<
    BrandWithdrawalRequest[]
  >([]);

  const columns: GridColDef<BrandWithdrawalRequest>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'created_at',
      headerName: 'Requested On',
      width: 180,
      type: 'dateTime',
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      type: 'number',
      // Withdrawals are stored as negative amounts; show the requested value.
      renderCell: (param) => `$${Math.abs(param.row.amount).toFixed(2)}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 320,
      type: 'string',
    },
  ];

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase
        .schema('admin_backend')
        .from('brand_withdrawal_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching brand withdrawal requests:', error);
        throw error;
      }

      setWithdrawalRequests(data);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout
      title="Withdrawal Requests"
      actions={
        <Button
          startIcon={<GridAddIcon />}
          size="small"
          variant="contained"
          onClick={() => {
            dialog.handleDialogOpen(
              <CreateWithdrawalRequestDialog onCreated={getData} />,
              'New Withdrawal Request',
            );
          }}>
          New Withdrawal Request
        </Button>
      }>
      <DataGrid columns={columns} rows={withdrawalRequests} />
      <Outlet />
    </PageBaseLayout>
  );
}
